/*
 * Formatos de presentación y captura compartidos por las pantallas migradas.
 * Lo que se guarda no cambia: teléfono en 10 dígitos y fechas en ISO
 * (AAAA-MM-DD); estos helpers solo traducen para mostrar y capturar.
 */

import { etiquetaEstado } from '@/utils/estados'

function soloDigitos(valor) {
  return String(valor ?? '').replace(/\D/g, '')
}

// Ladas de dos dígitos (CDMX, Guadalajara, Monterrey). El resto usa tres.
const LADAS_DOS_DIGITOS = ['33', '55', '56', '81']

/*
 * Máscara de captura de teléfono: (667) 319 0560 · (55) 1234 5678.
 * Acepta texto parcial; los separadores aparecen conforme se teclea y no
 * se agregan por adelantado, para que borrar hacia atrás funcione.
 */
export function mascaraTelefono(valor) {
  const d = soloDigitos(valor).slice(0, 10)
  if (!d) return ''
  const largoLada = LADAS_DOS_DIGITOS.includes(d.slice(0, 2)) ? 2 : 3
  if (d.length <= largoLada) return `(${d}`
  const resto = d.slice(largoLada)
  const primerGrupo = largoLada === 3 ? 3 : 4
  let texto = `(${d.slice(0, largoLada)}) ${resto.slice(0, primerGrupo)}`
  if (resto.length > primerGrupo) texto += ` ${resto.slice(primerGrupo)}`
  return texto
}

/*
 * Teléfono guardado → "(667) 319 0560". Acepta separadores y el prefijo
 * +52. Si no son 10 dígitos, regresa el valor tal cual para no esconder
 * datos históricos raros.
 */
export function formatearTelefono(valor) {
  if (valor === null || valor === undefined) return ''
  const texto = String(valor).trim()
  let digitos = soloDigitos(texto)
  if (digitos.length === 12 && digitos.startsWith('52')) digitos = digitos.slice(2)
  if (digitos.length !== 10) return texto
  return mascaraTelefono(digitos)
}

/*
 * Fecha guardada en Mongo → "10/01/2026".
 * - Marcas de tiempo reales (createdAt): en hora local. Un alta a las 6 pm
 *   en Culiacán es 01:00 UTC del día siguiente; en UTC saldría un día después.
 * - Fechas sin hora guardadas a medianoche UTC (received_at): con { utc: true }.
 */
export function formatearFechaNumerica(fecha, { utc = false } = {}) {
  if (!fecha) return ''
  const parsed = new Date(fecha)
  if (Number.isNaN(parsed.getTime())) return ''
  const dia = utc ? parsed.getUTCDate() : parsed.getDate()
  const mes = (utc ? parsed.getUTCMonth() : parsed.getMonth()) + 1
  const anio = utc ? parsed.getUTCFullYear() : parsed.getFullYear()
  return `${String(dia).padStart(2, '0')}/${String(mes).padStart(2, '0')}/${anio}`
}

/* Máscara de captura DD/MM/AAAA: "20051990" → "20/05/1990". */
export function mascaraFecha(valor) {
  const d = soloDigitos(valor).slice(0, 8)
  if (d.length <= 2) return d
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`
}

/* "20/05/1990" → "1990-05-20"; null si está incompleta o no existe (31/02). */
export function fechaTextoAISO(texto) {
  const partes = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(texto ?? '').trim())
  if (!partes) return null
  const [, dia, mes, anio] = partes
  const fecha = new Date(Date.UTC(Number(anio), Number(mes) - 1, Number(dia)))
  const valida =
    fecha.getUTCFullYear() === Number(anio) &&
    fecha.getUTCMonth() === Number(mes) - 1 &&
    fecha.getUTCDate() === Number(dia)
  return valida ? `${anio}-${mes}-${dia}` : null
}

/* "1990-05-20" → "20/05/1990". */
export function isoAFechaTexto(iso) {
  const partes = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''))
  return partes ? `${partes[3]}/${partes[2]}/${partes[1]}` : ''
}

// Partículas que van en minúscula dentro de un nombre: "María de la Luz"
const PARTICULAS = ['de', 'del', 'la', 'las', 'los', 'y', 'el']

// Abreviaturas de dirección que se escriben siempre igual
const ABREVIATURAS = { 's/n': 'S/N', sn: 'S/N', cp: 'C.P.', 'c.p.': 'C.P.', 'c.p': 'C.P.' }

function capitalizarPalabras(valor, { abreviaturas = {} } = {}) {
  const palabras = String(valor ?? '').trim().split(/\s+/).filter(Boolean)
  return palabras
    .map((palabra, i) => {
      const minuscula = palabra.toLocaleLowerCase('es-MX')
      if (abreviaturas[minuscula]) return abreviaturas[minuscula]
      if (i > 0 && PARTICULAS.includes(minuscula)) return minuscula
      return minuscula.replace(/(^|[-'’.])(\p{L})/gu, (_, sep, letra) => sep + letra.toLocaleUpperCase('es-MX'))
    })
    .join(' ')
}

/*
 * "ANA LÓPEZ DE LA PAZ" / "ana lópez" → "Ana López de la Paz".
 * También capitaliza tras guion o apóstrofo: "María-José", "O'Brien".
 */
export function capitalizarNombre(valor) {
  return capitalizarPalabras(valor)
}

/* "AV. OBREGON 100 S/N COL CENTRO" → "Av. Obregon 100 S/N Col Centro". */
export function capitalizarDireccion(valor) {
  return capitalizarPalabras(valor, { abreviaturas: ABREVIATURAS })
}

/*
 * Domicilio fiscal estructurado → dirección legible en dos líneas:
 *   { linea1: "Av. Obregón 100 Int. 4", linea2: "Col. Centro, C.P. 80000, Culiacán, Sinaloa" }
 * `texto` las une en una sola línea (la que imprime el contrato de la orden).
 */
export function componerDireccion(datos = {}) {
  const limpio = (v) => capitalizarDireccion(v)
  const colonia = limpio(datos.colonia).replace(/^col(onia)?\.?\s+/i, '')
  const linea1 = [
    limpio(datos.calle),
    limpio(datos.numero_exterior),
    datos.numero_interior ? `Int. ${limpio(datos.numero_interior)}` : '',
  ]
    .filter(Boolean)
    .join(' ')
  const linea2 = [
    colonia ? `Col. ${colonia}` : '',
    datos.codigo_postal ? `C.P. ${String(datos.codigo_postal).trim()}` : '',
    limpio(datos.municipio),
    // Nombre del catálogo con acentos ("Nuevo León"); si no es un estado, como venga
    etiquetaEstado(datos.estado) || limpio(datos.estado),
  ]
    .filter(Boolean)
    .join(', ')
  return { linea1, linea2, texto: [linea1, linea2].filter(Boolean).join(', ') }
}

/*
 * Dirección de un cliente para la lista, en dos líneas. Usa la dirección
 * capturada (la del contrato); si no hay, arma la fiscal. Sin datos → null.
 */
export function direccionDeCliente(cliente = {}) {
  const libre = capitalizarDireccion(cliente.address)
  if (libre) {
    const coma = libre.indexOf(',')
    return coma === -1
      ? { linea1: libre, linea2: '' }
      : { linea1: libre.slice(0, coma).trim(), linea2: libre.slice(coma + 1).trim() }
  }
  const fiscal = componerDireccion(cliente)
  return fiscal.texto ? { linea1: fiscal.linea1 || fiscal.linea2, linea2: fiscal.linea1 ? fiscal.linea2 : '' } : null
}

/*
 * Nombre de marca para mostrar: "LAND ROVER" → "Land Rover", pero las siglas
 * y lo que lleva números se quedan como están ("BMW", "GMC", "WD-40", "CX-5",
 * "SHARK W.P."). "AIR FILTER" → "Air Filter"; "KIA" → "Kia".
 */
// Marcas con mayúsculas propias que la regla general no deduce
const ESCRITURA_DE_MARCA = { ACDELCO: 'ACDelco' }

export function nombreDeMarca(valor) {
  const limpio = String(valor ?? '').trim().replace(/\s+/g, ' ')
  if (ESCRITURA_DE_MARCA[limpio.toUpperCase()]) return ESCRITURA_DE_MARCA[limpio.toUpperCase()]
  return limpio
    .split(/\s+/)
    .filter(Boolean)
    .map((palabra) => {
      const letras = palabra.replace(/[^\p{L}]/gu, '')
      // Siglas: cortas y con a lo más una vocal (BMW, GMC, SKF); "AIR" o "KIA" no lo son
      const vocales = (letras.match(/[aeiouáéíóú]/gi) || []).length
      const esSigla = letras.length <= 3 && vocales <= 1
      if (/\d/.test(palabra) || esSigla) return palabra.toLocaleUpperCase('es-MX')
      return palabra
        .toLocaleLowerCase('es-MX')
        .replace(/(^|[-'’])(\p{L})/gu, (_, sep, letra) => sep + letra.toLocaleUpperCase('es-MX'))
    })
    .join(' ')
}

/*
 * Nombre de modelo para mostrar: "GRAND CHEROKEE" → "Grand Cherokee", pero
 * los códigos se quedan en mayúsculas: lo que no tiene vocales (MDX, CRV),
 * una sola letra, lo corto pegado a números (CX-5, F150, NP300, GLE43) y
 * siglas conocidas (GTI, AWD). "4RUNNER" → "4Runner"; "X-TERRA" → "X-Terra".
 */
const SIGLAS_DE_MODELO = new Set(['GTI', 'GLI', 'ATS', 'LUV', 'AWD', 'ASX', 'JAC', 'AMG', 'SUV'])

export function nombreDeModelo(valor) {
  const limpio = String(valor ?? '').trim().replace(/\s+/g, ' ').toLocaleUpperCase('es-MX')
  return limpio.replace(/\p{L}+/gu, (letras, inicio) => {
    const pegadoANumero = /\d/.test(limpio[inicio - 1] || '') || /\d/.test(limpio[inicio + letras.length] || '')
    const sinVocales = !/[AEIOUÁÉÍÓÚÜ]/.test(letras)
    if (letras.length === 1 || sinVocales || SIGLAS_DE_MODELO.has(letras) || (pegadoANumero && letras.length <= 3)) {
      return letras
    }
    return letras[0] + letras.slice(1).toLocaleLowerCase('es-MX')
  })
}

/*
 * Nombre de empresa para mostrar: como las marcas (siglas y códigos se
 * quedan: "MORSA SA" → "Morsa SA") pero con las partículas en minúscula:
 * "AUTOZONE DE MEXICO" → "Autozone de Mexico", "S.A. DE C.V." → "S.A. de C.V.".
 */
export function nombreDeEmpresa(valor) {
  return nombreDeMarca(valor)
    .split(' ')
    .map((palabra, i) => {
      const minuscula = palabra.toLocaleLowerCase('es-MX')
      return i > 0 && PARTICULAS.includes(minuscula) ? minuscula : palabra
    })
    .join(' ')
}

/*
 * Nombre completo de una persona a partir de sus partes, capitalizado y sin
 * relleno: las partes sin letras ("." o "-" capturados como apellido) se
 * omiten. { name: 'CUAUHTEMOC ', first_lastname: '.' } → "Cuauhtemoc".
 */
export function nombreDePersona(persona = {}) {
  return [persona.name, persona.first_lastname, persona.second_lastname]
    .map((parte) => String(parte ?? '').trim())
    .filter((parte) => /\p{L}/u.test(parte))
    .map((parte) => capitalizarNombre(parte))
    .join(' ')
}

/*
 * Teléfonos guardados (texto o arreglo, a veces varios en un texto:
 * "6677175650, 6673582990") → lista con formato ["(667) 717 5650", …].
 */
export function telefonosDe(valor) {
  const lista = Array.isArray(valor) ? valor : [valor]
  return lista
    .flatMap((t) => String(t ?? '').split(/[,;/]|\s{2,}/))
    .map((t) => t.trim())
    .filter((t) => /\d/.test(t))
    .map((t) => formatearTelefono(t))
}

/*
 * Importes en MXN con dos decimales: 28940 → "$28,940.00". Sin dato
 * (null, vacío o no numérico) → "": la pantalla decide qué mostrar.
 */
const DINERO = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })

export function formatearDinero(valor) {
  if (valor === null || valor === undefined || valor === '') return ''
  const numero = Number(valor)
  return Number.isFinite(numero) ? DINERO.format(numero) : ''
}

/* Cantidades y kilómetros con separador de miles: 84310 → "84,310"; 4.5 → "4.5". */
export function formatearNumero(valor, { decimales = 2 } = {}) {
  if (valor === null || valor === undefined || valor === '') return ''
  const numero = Number(valor)
  if (!Number.isFinite(numero)) return ''
  return numero.toLocaleString('es-MX', { maximumFractionDigits: decimales })
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

/* Fecha → "marzo de 2022". Mismo criterio de zona que formatearFechaNumerica. */
export function mesYAnio(fecha, { utc = false } = {}) {
  if (!fecha) return ''
  const parsed = new Date(fecha)
  if (Number.isNaN(parsed.getTime())) return ''
  const mes = utc ? parsed.getUTCMonth() : parsed.getMonth()
  const anio = utc ? parsed.getUTCFullYear() : parsed.getFullYear()
  return `${MESES[mes]} de ${anio}`
}

/* Días completos (los calcula la API) → "Hoy", "Ayer", "Hace 75 días". */
export function haceDias(dias) {
  if (dias === null || dias === undefined || dias === '') return ''
  const n = Number(dias)
  if (!Number.isFinite(n) || n < 0) return ''
  if (n === 0) return 'Hoy'
  if (n === 1) return 'Ayer'
  return `Hace ${formatearNumero(Math.floor(n), { decimales: 0 })} días`
}

/*
 * Texto capturado en mayúsculas → oración: "CAMBIO DE ACEITE Y FRENOS" →
 * "Cambio de aceite y frenos". Los códigos se quedan como están: lo que
 * lleva números ("5W30", "LTH-149", "PH7317") o no tiene vocales ("ABS").
 * Mayúscula al inicio y después de punto.
 */
export function textoOracion(valor) {
  const limpio = String(valor ?? '').trim().replace(/\s+/g, ' ')
  if (!limpio) return ''
  const palabras = limpio.split(' ').map((palabra) => {
    const letras = palabra.replace(/[^\p{L}]/gu, '')
    const esCodigo = /\d/.test(palabra) || (letras.length > 1 && !/[aeiouáéíóúü]/i.test(letras))
    return esCodigo ? palabra.toLocaleUpperCase('es-MX') : palabra.toLocaleLowerCase('es-MX')
  })
  return palabras
    .join(' ')
    .replace(/(^[^\p{L}\d]*|[.!?]\s+[^\p{L}\d]*)(\p{L})/gu, (_, antes, letra) => antes + letra.toLocaleUpperCase('es-MX'))
}
